import React, { useRef } from "react";
import {
  DateRangePicker,
  DateInput,
  DateRangePickerChangeEvent,
  SelectionRange,
} from "@progress/kendo-react-dateinputs";
import { Button } from "@progress/kendo-react-buttons";
import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { Row, Col } from "react-bootstrap";
import { Trans } from "react-i18next";
import { t } from "i18next";
import moment from "moment";
import SearchTags, { SearchTagHandle } from "../../../components/SearchTags";
import PostCardGrid, { Post } from "components/DataLabeling/Missings/PostCardGrid";
import { ODataProvider } from "../../../contexts/ODataContext";
import config from "../../../contexts/ConfigContext";
import { SearchMissingsService } from "../../../services/Missings/SearchMissingsService";
import FileUploadButton, { FileUploadRef } from "components/DataLabeling/Missings/FileUploadButton";

function MissingPostsSearch() {
  const [valueDateRange, setValueDateRange] = React.useState<SelectionRange>({
    start: new Date(new Date().setDate(new Date().getDate() - 30)),
    end: new Date(),
  });
  const searchMissingsService = new SearchMissingsService();
  const [uniqueId, setUniqueId] = React.useState<string>("");
  const [valueSource, setValueSource] = React.useState<any[]>([]);
  const [, setFilterTypeSource] = React.useState<number>(1);
  const [valueSourceType, setValueSourceType] = React.useState<any[]>([]);
  const [, setFilterTypeSourceType] = React.useState<number>(1);
  const [valueCountry, setValueCountry] = React.useState<any[]>([]);
  const [, setFilterTypeCountry] = React.useState<number>(1);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const uploadRef = useRef<FileUploadRef>(null);
  const sourceApiUrl = config.API_URL + "/odata/ProjectSources/"; // Blogger
  const sourceTypeApiUrl = config.API_URL + "/odata/ProjectSourceTypes/"; // Channels
  const countryApiUrl = config.API_URL + "/odata/Countries/"; // Countries
  const sourcesDropDownRef = useRef<SearchTagHandle>(null);
  const sourceTypeDropDownRef = useRef<SearchTagHandle>(null);
  const countryDropDownRef = useRef<SearchTagHandle>(null);
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [showPostsModal, setShowPostsModal] = React.useState<boolean>(false); 
  const [showPostsLoading, setShowPostsLoading] = React.useState<boolean>(false); 

  // Clear all filters
  const clearFilters = () => {
    setValueDateRange({ start: valueDateRange.start, end: valueDateRange.end });
    sourcesDropDownRef?.current?.setSelectedValue([]);
    sourceTypeDropDownRef?.current?.setSelectedValue([]);
    countryDropDownRef?.current?.setSelectedValue([]);
    setUniqueId("");
    setUploadedFile(null);
    uploadRef.current?.clearFile();
  };

  // Handle the change of the date range
  const handleDateChange = (
    event: DateRangePickerChangeEvent,
    key: 'publishDate' | 'lastUpdateDate'
  ) => {
      setValueDateRange(event.value);
  };

  const PublishStartDateInput = (props: any) => {
    return (
      <label style={{ textAlign: "center" }}>
        <DateInput
          {...props}
          label={undefined}
          placeholder={t("general.start")}
          value={props.start}
        />
      </label>
    );
  };

  const PublishEndDateInput = (props: any) => {
    return (
      <label style={{ textAlign: "center" }}>
        <DateInput
          {...props}
          label={undefined}
          placeholder={t("general.end")}
          value={props.end}
        />
      </label>
    );
  };

  const handleFilterTypeSourceChange = (filterType: number) => {
    setFilterTypeSource(filterType);
  };

  // Handle the change of the blogger filter
  const handleValueSourceChange = (event: MultiSelectChangeEvent) => {
    setValueSource(event.target.value);
  };

  const handleFilterTypeSourceTypeChange = (filterType: number) => {
    setFilterTypeSourceType(filterType);
  };

  // Handle the change of the channel filter
  const handleValueSourceTypeChange = (event: MultiSelectChangeEvent) => {
    setValueSourceType(event.target.value);
  };

  const handleFilterTypeCountryChange = (filterType: number) => {
    setFilterTypeCountry(filterType);
  };

  // Handle the change of the country filter
  const handleValueCountryChange = (event: MultiSelectChangeEvent) => {
    setValueCountry(event.target.value);
  };

  const handleSearch = async () => {
    setShowPostsModal(true);
    setShowPostsLoading(true);
     await searchMissingsService
    .ExecuteSearchbyFilters({
      PeriodStartDate: valueDateRange.start ? moment(valueDateRange.start).startOf('day').toISOString() : null,
      PeriodEndDate: valueDateRange.end ? moment(valueDateRange.end).endOf('day').toISOString() : null,
      Sources: valueSource.map((x) => x.SourceId),
      Channels: valueSourceType.map((x) => x.SourceTypeId),
      Countries: valueCountry.map((x) => x.CountryId),
      UniqueId: uniqueId ?? ""
    },
      uploadedFile
  )
    .then((response: any) => {
      setShowPostsLoading(false);
      setPosts(response);
    })
    .catch((error: any) => {
      setShowPostsLoading(false);
      setShowPostsModal(false);
      console.error("Error searching: ", error);
    });
  };

  return (
    <div id="search">
      <div className="pt-4">
        <div className="tile-content">
          <h5 className="title">
            <Trans>dataLabeling.missings.searchTitle</Trans>
          </h5>
          <Row className="pt-2">
            <Col md={14} className="row">
              <Col sm={3}>
                <fieldset>
                  <div className="subtitle">
                    {" "}
                    <Trans>dataLabeling.missings.filters.period</Trans>
                  </div>
                  <DateRangePicker
                    format={"dd-MMM-yyyy"}
                    startDateInput={(props) => (
                      <PublishStartDateInput {...props} {...valueDateRange} />
                    )}
                    endDateInput={(props) => (
                      <PublishEndDateInput {...props} {...valueDateRange} />
                    )}
                    onChange={e => handleDateChange(e, 'publishDate')}
                  />
                </fieldset>
              </Col>
              <Col md={2}>
                <fieldset>
                  <ODataProvider
                    baseUrl={sourceApiUrl}
                    loadingContent=".k-multiselect"
                  >
                    <SearchTags
                      KeyField={"SourceId"}
                      Label={t("dataLabeling.search.filters.sources")}
                      PageSize={200}
                      PlaceHolder={"Search Sources"}
                      width={"100%"}
                      ref={sourcesDropDownRef}
                      customedTags={true}
                      enableFilter={false}
                      TextField={"SourceDescription"}
                      isDisabled={uploadedFile != null}
                      onFilterTypeChange={handleFilterTypeSourceChange}
                      onSetValue={handleValueSourceChange}
                    />
                  </ODataProvider>
                </fieldset>
              </Col>
              <Col sm={2}>
                <fieldset>
                  <ODataProvider
                    baseUrl={sourceTypeApiUrl}
                    loadingContent=".k-multiselect"
                  >
                    <SearchTags
                      KeyField={"SourceTypeId"}
                      Label={t("dataLabeling.search.filters.channels")}
                      PageSize={60}
                      PlaceHolder={"Search channels"}
                      width={"100%"}
                      ref={sourceTypeDropDownRef}
                      customedTags={true}
                      enableFilter={false}
                      TextField={"Description"}
                      isDisabled={uploadedFile != null}
                      onFilterTypeChange={handleFilterTypeSourceTypeChange}
                      onSetValue={handleValueSourceTypeChange}
                    />
                  </ODataProvider>
                </fieldset>
              </Col>
              <Col sm={2}>
                <fieldset>
                  <ODataProvider
                    baseUrl={countryApiUrl}
                    loadingContent=".k-multiselect"
                  >
                    <SearchTags
                      KeyField={"CountryId"}
                      Label={t("dataLabeling.search.filters.countries")}
                      PageSize={60}
                      PlaceHolder={"Search countries"}
                      width={"100%"}
                      ref={countryDropDownRef}
                      customedTags={true}
                      enableFilter={false}
                      TextField={"Description"}
                      isDisabled={uploadedFile != null}
                      onFilterTypeChange={handleFilterTypeCountryChange}
                      onSetValue={handleValueCountryChange}
                    />
                  </ODataProvider>
                </fieldset>
              </Col>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col sm={2}>
              <fieldset>
                <div className="subtitle"><Trans>dataLabeling.missings.filters.uniqueId</Trans></div>
                <Input
                  placeholder="Type Unique Id"
                  onChange={(e: InputChangeEvent) => setUniqueId(e.value)}
                  value={uniqueId??""}
                />
              </fieldset>
            </Col>
            <Col sm={3}>
              <fieldset>
                <div className="subtitle"><Trans>dataLabeling.missings.filters.uploadIds</Trans></div>
                <FileUploadButton onFileChange={setUploadedFile} uplFile={uploadedFile} ref={uploadRef} />
              </fieldset>
            </Col>
            <Col className="d-flex flex-column justify-content-end mt-3">
              <div className="d-flex gap-2 k-buttons-end">
                  <Button themeColor="secondary" onClick={clearFilters}>
                    <SvgIcon
                      className="btn-icons"
                      icon={svgIcons.xIcon}
                      size="medium"
                    />
                    <Trans>general.clear</Trans>
                  </Button>
                  <Button themeColor="primary" onClick={handleSearch}>
                    <SvgIcon
                      className="btn-icons"
                      icon={svgIcons.searchIcon}
                      size="medium"
                    />
                    <Trans>general.search</Trans>
                  </Button>
              </div>
            </Col> 
          </Row>
        </div>
      </div>
      <div className="pt-4">
        {(showPostsModal && <>
          <div className="tile-content">
          <Row>
            {(showPostsLoading && <Col sm={12}>
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <span className="k-icon k-i-loading k-spin" style={{ fontSize: "48px" }}></span>
              </div>
            </Col>)}

            {(!showPostsLoading &&
            <Col sm={12}>
              {/*cards component here*/ }
              <PostCardGrid PeriodStartDate={moment(valueDateRange.start).startOf('day').toISOString()} PeriodEndDate={moment(valueDateRange.end).endOf('day').toISOString()} posts={posts}/>
            </Col>)}
          </Row>
        </div>
        </>)}
      </div>
    </div>
  );
}

export default MissingPostsSearch;
